/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { TDialogComponent } from "../../../dialogs/Dialog";
import { DialogHeader } from "../../../ui/components/Dialogs/DialogHeader";
import { DIALOG_SIZE, DialogPanel } from "../../../ui/components/Dialogs/DialogPanel";
import { AuthenticateDialog } from "./AuthenticateDialog";
import { DialogContent } from "../../../ui/components/Dialogs/DialogContent";
import { Form } from "../../../ui/components/Form";
import { FieldText } from "../../../ui/components/Fields/FieldText";
import { useFieldState, useStateWithEvent } from "../../../ui/hooks";
import { DialogButtons } from "../../../ui/components/Dialogs/DialogButtons";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../../ui/components/ActionButton";
import { Typography } from "../../../ui/components/Typography";
import { FieldRow } from "../../../ui/components/Fields/FieldRow";
import { FieldGroup } from "../../../ui/components/Fields/FieldGroup";

export const AuthenticateDialogComponent: TDialogComponent<AuthenticateDialog> = ({ dialog }) => {
	const state = useStateWithEvent(dialog.onStatusChange, () => ({
		status: dialog.getStatus(),
		lastError: dialog.getLastError(),
		hasInitialAccessToken: !!dialog.hasInitialAccessToken()
	}));

	const [ fieldState, setFieldState ] = useFieldState(
		{
			accessToken: {
				defaultValue: ""
			}
		},
		[]
	);

	const handleSubmit = useCallback(() => {
		if (dialog.hasInitialAccessToken()) {
			dialog.authenticateWithInitialToken();
		} else {
			dialog.authenticate(fieldState.accessToken.value);
		}
	}, [ dialog, fieldState ]);

	if (state.hasInitialAccessToken) {
		return (
			<DialogPanel size={DIALOG_SIZE.SMALL}>
				<Form onSubmit={handleSubmit} noValidate>
					<DialogHeader title="Session Expired" icon="mdi/lock" />
					{state.status === "failure" && (
						<DialogContent>
							<Typography Component="div">
								Failed to restore session with initial access token. Please restart the entire editor.
							</Typography>
						</DialogContent>
					)}
					<DialogContent>
						<Typography Component="div">
							You session has expired, probably because the editor back-end was restarted. Please click the button below to restore your session.
						</Typography>
					</DialogContent>
					<DialogButtons>
						<ActionButton
							label="Restore Session"
							icon="mdi/send"
							type="submit"
							style={ACTION_BUTTON_STYLE.PRIMARY}
							size={ACTION_BUTTON_SIZE.LARGE}
							isLoading={state.status === "loading"}
						/>
					</DialogButtons>
				</Form>
			</DialogPanel>
		);
	} else {
		return (
			<DialogPanel size={DIALOG_SIZE.SMALL}>
				<Form onSubmit={handleSubmit} noValidate>
					<DialogHeader title="Authenticate" icon="mdi/lock" />
					<DialogContent>
						<FieldRow className="edt-w-full">
							<FieldGroup>
								<FieldText
									type="password"
									value={fieldState.accessToken.value}
									validationErrors={fieldState.accessToken.validationErrors}
									placeholder="Please enter your access token"
									onChange={setFieldState.accessToken}
									className="w-full"
								/>
							</FieldGroup>
						</FieldRow>
					</DialogContent>
					{state.status === "failure" && (
						<DialogContent>
							<Typography Component="div">
							Invalid credentials.
							</Typography>
						</DialogContent>
					)}
					<DialogButtons>
						<ActionButton
							label="Authenticate"
							icon="mdi/send"
							type="submit"
							style={ACTION_BUTTON_STYLE.PRIMARY}
							size={ACTION_BUTTON_SIZE.LARGE}
							disabled={!fieldState.accessToken.value}
							isLoading={state.status === "loading"}
						/>
					</DialogButtons>
				</Form>
			</DialogPanel>
		);
	}
};
